import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchData } from "../../utils";
import { setNotification } from "../notificationsSlice";
import { has, groupBy, sortBy } from "lodash";
import { API_ARCHIVE_PACKAGE, API_MASTER_KIT_LIST, API_PACKAGE, API_PACKAGE_DOC_LINK, API_PACKAGE_DOC_LINKS, API_PACKAGES, API_PACKAGES_MASS_UPDATE, API_PACKAGES_SEARCH, API_VERIFY_PACKAGE } from "../../settings";
import { schemas } from '../schemas';


export const getPackageDetails = createAsyncThunk(
    "getPackageDetails",
    async(packageNumber, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_PACKAGE, {
                headers: { "X-Pkg-Id": packageNumber }
            }, schemas.PACKAGE);
            if (has(response, "entities") && has(response.entities, "packages")) {
                dispatch(getPackageDocumetationLinks(packageNumber));
                return response.entities.packages[packageNumber];
            };
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({ code: e.code });
        };
        return rejectWithValue({});
    }
);

export const verifyPackage = createAsyncThunk(
    "verifyPkg",
    async({ id, verify, comment }, { rejectWithValue }) => {
        try {
            const response = await fetchData(API_VERIFY_PACKAGE, {
                method: "put",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    "package_number": id,
                    "verify": verify,
                    "comment": comment
                })
            }, {});
            return response.result;
        } catch (e) {
            return rejectWithValue(e);
        };
    }
);

export const deletePackage = createAsyncThunk(
    "deletePkg",
    async(packageNumber, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_PACKAGE, {
                method: "delete",
                headers: {
                    "X-Pkg-Id": packageNumber
                }
            }, {});
            dispatch(setNotification(response.result));
            return response.result;
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue(e)
        };
    }
);

export const getPackageDocumetationLinks = createAsyncThunk(
    "getPackageDocumetationLinks",
    async(packageNumber, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_PACKAGE_DOC_LINKS(packageNumber), {}, {});
            return response.hasOwnProperty("result")
                ?  Object.values(response.result)
                : [];
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

export const addPackageDocumetationLink = createAsyncThunk(
    "addPackageDocumetationLink",
    async({ packageNumber, data }, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_PACKAGE_DOC_LINK(packageNumber), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data)
            }, {});
            if (response.hasOwnProperty("result")) {
                dispatch(setNotification({ success: true, message: "Documentation link successfully added"}));
                return response.result;
            }
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

export const editPackageDocumetationLink = createAsyncThunk(
    "editPackageDocumetationLink",
    async({ packageNumber, id, description, link }, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_PACKAGE_DOC_LINK(packageNumber) + "/" + id, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ description: description, link: link })
            }, {});
            if (response.hasOwnProperty("result")) {
                dispatch(setNotification({ success: true, message: "Documentation link  successfully updated"}));
                return response.result;
            }
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

export const deletePackageDocumetationLink = createAsyncThunk(
    "deletePackageDocumetationLink",
    async({ id, packageNumber }, { dispatch }) => {
        try {
            const response = await fetchData(API_PACKAGE_DOC_LINK(packageNumber) + "/" + id, {
                method: "DELETE"
            }, {});
            dispatch(getPackageDocumetationLinks(packageNumber));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const getMasterKitList = createAsyncThunk(
    "getMasterKitList",
    async({ params={} }, { dispatch, rejectWithValue }) => {
        try {
            let search = "?limit=1000&verified=1&filtered=1";
            Object.keys(params).map(key => {
                search = search + "&" + [key, params[key]].join("=")
            })
            const response = await fetchData(API_MASTER_KIT_LIST, {}, schemas.MASTER_KIT_LIST);
            if (response.entities && response.entities.packages) {
                return Object.values(response.entities.packages)[0];
            }
            return rejectWithValue();
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const downloadMasterKitList = createAsyncThunk(
    "downloadMasterKitList",
    async(arg, { dispatch }) => {
        try {
            const response = await fetchData(API_MASTER_KIT_LIST + "/download", {}, {});
            let a = document.createElement("a");
            a.download = response.result.saveAs;
            a.href = response.result.url;
            a.rel = "noopener noreferrer";
            a.target = "_blank";
            a.click();
            a.remove();
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const getPackagesByIds = createAsyncThunk(
    "getPackagesByIds",
    async(ids, { dispatch }) => {
        try {
            const response = await fetchData(API_PACKAGES_SEARCH + "?ids=" + ids, {}, schemas.SEARCH_PACKAGES);
            return response.entities.packages;
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const updatePackages = createAsyncThunk(
    "updatePackages",
    async(values, { dispatch }) => {
        try {
            const response = await fetchData(API_PACKAGES_MASS_UPDATE, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(values)
            }, {});
            dispatch(setNotification(response.result));
            return { entity: "packages" };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const archivePackageVersion = createAsyncThunk(
    "archivePkg",
    async({ id }, { rejectWithValue }) => {
        try {
            const response = await fetchData(API_ARCHIVE_PACKAGE(id), {
                method: "put"
            }, {});
            return response.result;
        } catch (e) {
            return rejectWithValue(e);
        };
    }
);

export const getRecentPackages = createAsyncThunk(
    "getRecentPackages",
    async(arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_PACKAGES_SEARCH + "/recent", {}, schemas.SEARCH_PACKAGES);
            return response.entities.packages ? response.entities.packages : {};
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({});
        };
    }
);

export const getFuturePackages = createAsyncThunk(
    "getFuturePackages",
    async(arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(API_PACKAGES_SEARCH + "/future", {}, {});
            return groupBy(sortBy(Object.values(response.result), ["releaseDate"]), "productName");
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({});
        };
    }
)